import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { getOrderStatus, cancelOrder } from '../api';
import { useSession } from '../SessionContext';
import { Check, X, ChefHat, PartyPopper, CreditCard, ClipboardCheck, Flame, CircleCheckBig } from 'lucide-react';
import './OrderTracking.css';

const STATUS_CONFIG = {
  new:       { icon: ClipboardCheck, label: 'Order Received',  message: 'Your order has been received and is waiting to be prepared.', pulse: true },
  preparing: { icon: ChefHat,        label: 'Preparing',       message: 'The kitchen is working on your order.', pulse: true },
  cooking:   { icon: Flame,          label: 'Cooking',         message: "Your food is being cooked. Won't be long!", pulse: true },
  ready:     { icon: PartyPopper,    label: 'Your food is ready!', message: '', pulse: false },
  complete:  { icon: CircleCheckBig, label: 'Your food is ready!', message: '', pulse: false },
};

export default function OrderTracking({ order, venueId, onNewOrder }) {
  const { sessionToken } = useSession();

  const [orderStatus, setOrderStatus] = useState(order);
  const [cancelled, setCancelled] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const token = order?.session_token || sessionToken;
  const currentStatus = cancelled ? 'cancelled' : (orderStatus?.status || order?.status || 'new');

  // Polling
  useEffect(() => {
    if (!order) return;

    let active = true;

    const poll = setInterval(async () => {
      if (!active) return;
      try {
        const status = await getOrderStatus(order.order_id, token);
        if (active) {
          setOrderStatus(status);
          if (['ready', 'complete', 'cancelled'].includes(status.status)) {
            clearInterval(poll);
            if (status.status === 'cancelled') setCancelled(true);
          }
        }
      } catch (err) {
        console.error('Status poll failed:', err);
      }
    }, order.poll_interval || 10000);

    return () => {
      active = false;
      clearInterval(poll);
    };
  }, [order, token]);

  const handleCancel = async () => {
    if (cancelling) return;
    setCancelError(null);
    setCancelling(true);
    try {
      await cancelOrder(order.order_id, token);
      setCancelled(true);
    } catch (err) {
      setCancelError(err.message || 'Could not cancel — your order may already be preparing.');
    } finally {
      setCancelling(false);
    }
  };

  if (!order) return null;

  const isReady = ['ready', 'complete'].includes(currentStatus);
  const isCancelled = currentStatus === 'cancelled' || cancelled;
  const isNew = currentStatus === 'new' && !isCancelled;

  return (
    <div className={`order-tracking${isReady ? ' order-tracking--ready' : ''}${isCancelled ? ' order-tracking--cancelled' : ''}`}>

      {/* ── Header ── */}
      {!isCancelled && (
        <div className="ot-header">
          <p className="ot-header__label">Order</p>
          <p className="ot-header__number">#{order.order_number || order.order_id}</p>
          <p className="ot-header__table">Table {order.table_number}</p>
        </div>
      )}

      {/* ── Cancelled State ── */}
      {isCancelled && (
        <div className="ot-cancelled">
          <span className="ot-cancelled__icon"><X size={32} /></span>
          <h2 className="ot-cancelled__title">Order Cancelled</h2>
          <p className="ot-cancelled__sub">Your order has been cancelled.</p>
        </div>
      )}

      {/* ── Status Icon ── */}
      {!isCancelled && (() => {
        const config = STATUS_CONFIG[currentStatus] || STATUS_CONFIG.new;
        const StatusIcon = config.icon;
        return (
          <div className="ot-status-display">
            <div className={`ot-status-display__circle${config.pulse ? ' ot-status-display__circle--pulse' : ''} ot-status-display__circle--${currentStatus}`}>
              <StatusIcon size={48} strokeWidth={1.5} />
            </div>
            <h2 className="ot-status-display__label">{config.label}</h2>
            <p className="ot-status-display__message">{config.message}</p>
          </div>
        );
      })()}

      {/* ── Order Summary ── */}
      {!isCancelled && (orderStatus?.items || order?.items) && (
        <div className="ot-summary">
          <h3 className="ot-summary__title">Your Order</h3>
          <ul className="ot-summary__list">
            {(orderStatus?.items || order?.items).map((item, i) => (
              <li key={i} className="ot-summary__item">
                <span className="ot-summary__qty">{item.quantity}×</span>
                <span className="ot-summary__name">{item.name}</span>
              </li>
            ))}
          </ul>
          {order?.total != null && (
            <div className="ot-summary__total">
              <span>Total</span>
              <span>£{Number(order.total).toFixed(2)}</span>
            </div>
          )}
        </div>
      )}

      {/* ── Pay-at-counter QR code ── */}
      {!isCancelled && order.payment_status === 'pending' && (
        <div className="ot-pay-qr">
          <div className="ot-pay-qr__code">
            <QRCodeSVG
              value={`ATXMC${order.order_number || order.order_id}`}
              size={180}
              level="M"
            />
          </div>
          <div className="ot-pay-qr__info">
            <span className="ot-pay-qr__icon"><CreditCard size={20} /></span>
            <p className="ot-pay-qr__text">Show this QR code at the counter to pay.</p>
          </div>
        </div>
      )}

      {/* ── Cancel error ── */}
      {cancelError && (
        <div className="ot-cancel-error">
          <p>{cancelError}</p>
        </div>
      )}

      {/* ── Actions ── */}
      <div className="ot-actions">
        <button className="ot-btn-order-again" onClick={onNewOrder}>
          Order Again
        </button>
      </div>

    </div>
  );
}
